/**
 * CÓMO SE ENSEÑA UN DIAGNÓSTICO DE PLAGA POR FOTO — una sola regla para todas las
 * pantallas (usuario, 18-09-2026).
 *
 * El problema: la misma foto, con el mismo análisis guardado, salía en el chat
 * como «Virus de la cuchara, 52 %» y en el parte de campo como «No se reconoció
 * ninguna plaga». El parte escondía todo lo que bajaba de 60 % y, al esconderlo,
 * decía lo contrario de lo que había visto la IA.
 *
 * Tres estados, iguales en el parte, en el chat y en el escáner:
 *   · «diagnostico»  → ≥ 60 %: se enseña el diagnóstico.
 *   · «sospecha»     → algo hay, pero < 60 %: «Posible X, sin confirmar», con los
 *                      candidatos y qué foto lo confirmaría.
 *   · «nada»         → la IA de verdad no vio ninguna plaga. Solo entonces se dice.
 *
 * `limpiarNombrePlaga` tiene su gemelo en el servidor
 * (`supabase/functions/_shared/pestDecision.ts`); la prueba de paridad
 * (`pestDiagnosis.test.ts`) comprueba que los dos dicen lo mismo.
 */

/** A partir de aquí es un diagnóstico; por debajo, una sospecha. */
export const CONFIANZA_DIAGNOSTICO = 0.6;

export type EstadoDiagnostico = "diagnostico" | "sospecha" | "nada";

/** 0–1 aunque venga en 0–100 (análisis antiguos). */
export function normalizarConfianza(c: number | null | undefined): number {
  if (typeof c !== "number" || !Number.isFinite(c)) return 0;
  return c > 1 ? c / 100 : c;
}

/** `null` = todavía no hay análisis. */
export function estadoDiagnostico(
  a: { identified?: boolean; pestName?: string | null; confidence?: number | null } | null | undefined,
): EstadoDiagnostico | null {
  if (!a) return null;
  if (a.identified === false || !limpiarNombrePlaga(a.pestName)) return "nada";
  return normalizarConfianza(a.confidence) >= CONFIANZA_DIAGNOSTICO ? "diagnostico" : "sospecha";
}

/** ¿Hay algo que enseñar en la tarjeta (diagnóstico o sospecha)? */
export const seEnseña = (e: EstadoDiagnostico | null): boolean => e === "diagnostico" || e === "sospecha";

const DUDA =
  /\b(sospecha|sospechos[oa]|sin confirmar|no confirmad[oa]|posible|probable|presunt[oa]|suspected|unconfirmed|possible|likely)\b/i;

/**
 * El titular es un NOMBRE, nunca una frase con la duda dentro. El modelo a veces
 * escribe «Virus de la cuchara (TYLCV), sospecha sin confirmar»: la duda ya la
 * dice la confianza, así que aquí se queda «Virus de la cuchara (TYLCV)».
 */
export function limpiarNombrePlaga(nombre: string | null | undefined): string {
  const original = (nombre ?? "").trim();
  if (!original) return "";
  const trozos = original
    .split(/\s*[,;]\s*|\s+[—–-]\s+|\s+\/\s+/)
    .map((t) => t.trim())
    .filter(Boolean);
  let n = trozos.find((t) => !DUDA.test(t)) ?? trozos[0] ?? original;
  n = n.replace(/\s*\(([^)]*)\)/g, (m: string, dentro: string) => (DUDA.test(dentro) ? "" : m));
  n = n.replace(/^(posible|probable|presunt[oa]|sospecha de|possible|suspected|likely)\s+/i, "");
  n = n.replace(/\s+(no confirmad[oa]|sin confirmar|sospechos[oa]|unconfirmed|suspected)$/i, "");
  n = n.trim();
  return n || original;
}

/**
 * Qué más podría ser, para la tarjeta de «sospecha»: primero lo que dijo el otro
 * modelo, luego las primeras alternativas que consideró el que decidió (vienen
 * ordenadas de más a menos plausible). Máximo tres, sin repetir el titular.
 */
export function candidatosDe(opts: {
  otros?: unknown[] | null;
  consideradas?: unknown[] | null;
  principal?: string | null;
}, max = 3): string[] {
  const principal = limpiarNombrePlaga(opts.principal).toLowerCase();
  const nombres = [...(opts.otros ?? []), ...(opts.consideradas ?? []).slice(0, 2)]
    .map((a) => limpiarNombrePlaga(String(a ?? "").split(":")[0]))
    .filter((n) => !!n && n.toLowerCase() !== principal && n.toLowerCase() !== "healthy");
  return [...new Set(nombres)].slice(0, max);
}
